import React from 'react';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import {
  AddCircle as CreateIcon,
  Delete as BinIcon,
  Edit as EditIcon,
  CheckCircle as PublishIcon,
  Feedback as FeedbackIcon,
  Poll,
  Visibility,
  Email,
  AssignmentInd,
  Folder,
  AltRoute,
} from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { Icons } from '../../../Themes';
import CustomIcon from '../CustomIcon';
import { dashboardPostPath, editPostPath, postPath } from '../../../Utils/routes';
import { Groups } from '../../../Utils/Types';

export const NewConsultation = React.forwardRef(({ handleClick }: any, ref: any) => (
  <ListItemButton ref={ref} role="list" onClick={handleClick}>
    <ListItemIcon>
      <CreateIcon />
    </ListItemIcon>
    <ListItemText>
      <FormattedMessage id="DASHBOARD.MENU.CONSULTATION" />
    </ListItemText>
  </ListItemButton>
));

NewConsultation.displayName = 'NewConsultation';

export const NewCommunication = React.forwardRef(({ handleClick }: any, ref: any) => (
  <ListItemButton ref={ref} role="list" onClick={handleClick}>
    <ListItemIcon>
      <CreateIcon />
    </ListItemIcon>
    <ListItemText>
      <FormattedMessage id="DASHBOARD.MENU.COMMUNICATION" />
    </ListItemText>
  </ListItemButton>
));

NewCommunication.displayName = 'NewCommunication';

export const NewIdea = React.forwardRef(({ postId, spaceSlug }: any, ref: any) => (
  <ListItemButton
    ref={ref}
    role="list"
    to={`/${spaceSlug}/ideas/new?parent_id=${postId}`}
    component={Link}
  >
    <ListItemIcon>
      <CustomIcon src={Icons.misc.bulb} />
    </ListItemIcon>
    <ListItemText>
      <FormattedMessage id="POSTS.CREATE_MENU.NEW_IDEA" />
    </ListItemText>
  </ListItemButton>
));

NewIdea.displayName = 'NewIdea';

export const NewFolder = React.forwardRef(({ handleClick }: any, ref: any) => (
  <ListItemButton ref={ref} role="list" onClick={handleClick}>
    <ListItemIcon>
      <CreateIcon />
    </ListItemIcon>
    <ListItemText>
      <FormattedMessage id="DASHBOARD.MENU.FOLDER" />
    </ListItemText>
  </ListItemButton>
));

NewFolder.displayName = 'NewFolder';

export const Edit = React.forwardRef(({ postSlug, spaceSlug, postType }: any, ref: any) => (
  <ListItemButton
    ref={ref}
    role="list"
    to={editPostPath(spaceSlug, postSlug, postType)}
    component={Link}
  >
    <ListItemIcon>
      <EditIcon />
    </ListItemIcon>
    <ListItemText>
      <FormattedMessage id="POSTS.CREATE_MENU.EDIT" />
    </ListItemText>
  </ListItemButton>
));

Edit.displayName = 'Edit';

export const ViewInDashboard = React.forwardRef(
  ({ postSlug, spaceSlug, postType }: any, ref: any) => (
    <ListItemButton
      ref={ref}
      role="list"
      to={dashboardPostPath(spaceSlug, postSlug, postType)}
      component={Link}
    >
      <ListItemIcon>
        <Poll />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="POSTS.CREATE_MENU.VIEW_IN_DASHBOARD" />
      </ListItemText>
    </ListItemButton>
  ),
);

ViewInDashboard.displayName = 'ViewInDashboard';

export const ContactAuthor = React.forwardRef(
  ({ spaceSlug, postSlug, postType, onClick }: any, ref: any) => (
    <ListItemButton
      ref={ref}
      role="list"
      to={`${dashboardPostPath(spaceSlug, postSlug, postType)}/emails`}
      onClick={onClick}
      component={Link}
    >
      <ListItemIcon>
        <Email />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="POSTS.CREATE_MENU.CONTACT_AUTHOR" />
      </ListItemText>
    </ListItemButton>
  ),
);

ContactAuthor.displayName = 'ContactAuthor';

export const ViewPost = React.forwardRef(({ postSlug, spaceSlug }: any, ref: any) => (
  <ListItemButton
    ref={ref}
    role="list"
    to={postPath(spaceSlug, postSlug)}
    component={Link}
    target="_blank"
  >
    <ListItemIcon>
      <Visibility />
    </ListItemIcon>
    <ListItemText>
      <FormattedMessage id="POSTS.CREATE_MENU.VIEW_POST" />
    </ListItemText>
  </ListItemButton>
));

ViewPost.displayName = 'ViewPost';

export const Destroy = React.forwardRef(({ handleClick }: any, ref: any) => {
  return (
    <ListItemButton
      ref={ref}
      role="list"
      onClick={() => handleClick('destroy_post')}
      sx={{ color: 'error.light' }}
    >
      <ListItemIcon>
        <BinIcon sx={{ color: 'error.light' }} />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="BUTTONS.DELETE" />
      </ListItemText>
    </ListItemButton>
  );
});

Destroy.displayName = 'Destroy';

export const Clone = React.forwardRef(({ handleClick }: any, ref: any) => {
  return (
    <ListItemButton ref={ref} role="list" onClick={() => handleClick('clone_post')}>
      <ListItemIcon>
        <AltRoute />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="BUTTONS.CLONE" />
      </ListItemText>
    </ListItemButton>
  );
});

Clone.displayName = 'Clone';

export const AddToFolder = React.forwardRef(({ handleClick }: any, ref: any) => {
  return (
    <ListItemButton ref={ref} role="list" onClick={() => handleClick('addToFolder')}>
      <ListItemIcon>
        <Folder />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="POSTS.CREATE_MENU.ADD_TO_FOLDER" />
      </ListItemText>
    </ListItemButton>
  );
});

AddToFolder.displayName = 'AddToFolder';

export const Assign = React.forwardRef(({ handleClick }: any, ref: any) => {
  return (
    <ListItemButton ref={ref} role="list" onClick={() => handleClick('assign')}>
      <ListItemIcon>
        <AssignmentInd />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="POSTS.CREATE_MENU.ASSIGN" />
      </ListItemText>
    </ListItemButton>
  );
});

Assign.displayName = 'Assign';

export const UpdateStatus = React.forwardRef(({ handleClick }: any, ref: any) => {
  return (
    <ListItemButton ref={ref} role="list" onClick={() => handleClick('updateStatus')}>
      <ListItemIcon>
        <CustomIcon src={Icons.posts.status.change} size={24} />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="POSTS.CREATE_MENU.UPDATE_STATUS" />
      </ListItemText>
    </ListItemButton>
  );
});

UpdateStatus.displayName = 'UpdateStatus';

export const Merge = React.forwardRef(({ handleClick }: any, ref: any) => {
  return (
    <ListItemButton ref={ref} role="list" onClick={() => handleClick('updateStatus')}>
      <ListItemIcon>
        <CustomIcon src={Icons.misc.duplicate} size={24} />
      </ListItemIcon>
      <ListItemText>
        <FormattedMessage id="POSTS.CREATE_MENU.MERGE" />
      </ListItemText>
    </ListItemButton>
  );
});

Merge.displayName = 'Merge';

export const Report = React.forwardRef(({ handleClick, space }: any, ref: any) => {
  const willDelete = Groups.CREATORS.includes(space.current_user_role);
  return (
    <ListItemButton
      ref={ref}
      role="list"
      onClick={() => handleClick('report_post')}
      sx={{ color: 'error.light' }}
    >
      <ListItemIcon>
        <FeedbackIcon sx={{ color: 'error.light' }} />
      </ListItemIcon>
      <ListItemText>
        {willDelete ? (
          <FormattedMessage id="BUTTONS.DELETE" />
        ) : (
          <FormattedMessage id="POSTS.REPORT.ACTION" />
        )}
      </ListItemText>
    </ListItemButton>
  );
});

Report.displayName = 'Report';

export const PinUnpin = React.forwardRef(({ isPinned, handleClick, isFetching }: any, ref: any) => {
  const text = isPinned ? (
    <FormattedMessage id="POSTS.CREATE_MENU.UNPIN" />
  ) : (
    <FormattedMessage id="POSTS.CREATE_MENU.PIN" />
  );

  return (
    <ListItemButton ref={ref} role="list" onClick={handleClick}>
      <ListItemIcon>
        <CustomIcon src={isPinned ? Icons.misc.unPin : Icons.misc.pin} />
      </ListItemIcon>
      <ListItemText>
        {isFetching ? <FormattedMessage id="BUTTONS.IN_PROGRESS" /> : text}
      </ListItemText>
    </ListItemButton>
  );
});

PinUnpin.displayName = 'PinUnpin';

export const PublishUnpublish = React.forwardRef(
  ({ handleClick, isPublished, isFetching }: any, ref: any) => {
    const text = isPublished ? (
      <FormattedMessage id="POSTS.CREATE_MENU.UNPUBLISH" />
    ) : (
      <FormattedMessage id="BUTTONS.PUBLISH" />
    );

    return (
      <ListItemButton ref={ref} role="list" onClick={handleClick}>
        <ListItemIcon>
          {isPublished ? <CustomIcon src={Icons.misc.unpublish} /> : <PublishIcon />}
        </ListItemIcon>
        <ListItemText>
          {isFetching ? <FormattedMessage id="BUTTONS.IN_PROGRESS" /> : text}
        </ListItemText>
      </ListItemButton>
    );
  },
);

PublishUnpublish.displayName = 'PublishUnpublish';
